import m from "mithril";
import CoordMobileLayout from "./Coord-mobile-layout.js";
import { formatRupiah } from "../utils.js";
import dataLayer from "../pwa/data-layer.js";

const CoordMobileRekapPinjamanDetails = {
    installments: [],
    isLoading: false,
    error: null,
    productId: null,
    productName: '',
    startDate: '',
    endDate: '',
    filterApplied: false,

    async oninit(vnode) {
        this.productId = vnode.attrs.productId || m.route.param('productId');
        await this.loadProductName();
        await this.loadInstallments();
    },

    async loadProductName() {
        try {
            const product = await dataLayer.getLoanProductById(this.productId);
            if (product) {
                this.productName = product.name;
            }
        } catch (e) {
            console.error('Error loading product name:', e);
        }
    },

    async loadInstallments() {
        try {
            this.isLoading = true;
            this.error = null;
            m.redraw();

            // Build query parameters
            const params = {
                status: 'Paid',
                productId: this.productId
            };

            if (this.startDate) params.startDate = this.startDate;
            if (this.endDate) params.endDate = this.endDate;

            // Use dataLayer for cache-first reads with background refresh
            const data = await dataLayer.getRekapPinjaman(params);
            this.installments = data || [];
            this.filterApplied = !!(this.startDate || this.endDate);
        } catch (error) {
            console.error('Error loading installments:', error);
            this.error = 'Gagal memuat data angsuran: ' + error.message;
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    applyFilter() {
        this.loadInstallments();
    },

    clearFilter() {
        this.startDate = '';
        this.endDate = '';
        this.loadInstallments();
    },

    view() {
        const content = m("div", { class: "min-h-screen bg-gray-50 dark:bg-gray-900 pb-20" }, [
            // Header
            m("div", { class: "bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10" }, [
                m("div", { class: "px-4 py-3" }, [
                    m("div", { class: "flex items-center mb-2" }, [
                        m("button", {
                            class: "mr-3 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors",
                            onclick: () => m.route.set('/coord-rekap-pinjaman-mobile')
                        }, [
                            m("i", { class: "ri-arrow-left-line text-xl" })
                        ]),
                        m("div", [
                            m("h1", { class: "text-lg font-bold text-gray-800 dark:text-white" }, "Detail Angsuran"),
                            m("p", { class: "text-xs text-gray-600 dark:text-gray-300" }, this.productName || 'Produk Pinjaman')
                        ])
                    ]),

                    // Date Range Filter
                    m("div", { class: "bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mt-2" }, [
                        m("div", { class: "grid grid-cols-2 gap-2 mb-2" }, [
                            m("div", [
                                m("label", { class: "block text-xs text-gray-600 dark:text-gray-300 mb-1" }, "Dari Tanggal"),
                                m("input", {
                                    type: "date",
                                    class: "w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white",
                                    value: this.startDate,
                                    onchange: (e) => { this.startDate = e.target.value; m.redraw(); }
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-xs text-gray-600 dark:text-gray-300 mb-1" }, "Sampai Tanggal"),
                                m("input", {
                                    type: "date",
                                    class: "w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white",
                                    value: this.endDate,
                                    onchange: (e) => { this.endDate = e.target.value; m.redraw(); }
                                })
                            ])
                        ]),
                        m("div", { class: "flex gap-2" }, [
                            m("button", {
                                class: "flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors",
                                onclick: () => this.applyFilter()
                            }, "Terapkan"),
                            this.filterApplied ? m("button", {
                                class: "flex-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors",
                                onclick: () => this.clearFilter()
                            }, "Reset") : null
                        ])
                    ])
                ])
            ]),

            // Main Content
            m("div", { class: "p-4" }, [
                this.isLoading ? 
                    m("div", { class: "flex justify-center items-center py-12" }, [
                        m("i", { class: "ri-loader-4-line animate-spin text-3xl text-blue-600" })
                    ]) :
                    
                this.error ?
                    m("div", { class: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-center" }, [
                        m("i", { class: "ri-error-warning-line text-red-600 text-2xl mb-2" }),
                        m("p", { class: "text-sm text-red-800 dark:text-red-200" }, this.error),
                        m("button", {
                            class: "mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm",
                            onclick: () => this.loadInstallments()
                        }, "Coba Lagi")
                    ]) :

                this.installments.length === 0 ?
                    m("div", { class: "text-center py-12" }, [
                        m("i", { class: "ri-inbox-line text-gray-400 text-4xl mb-3" }),
                        m("p", { class: "text-gray-600 dark:text-gray-300 text-sm" }, 
                            this.filterApplied ? "Tidak ada data untuk periode yang dipilih" : "Belum ada angsuran yang lunas"
                        )
                    ]) :

                    m("div", { class: "space-y-3" }, [
                        // Summary Card
                        m("div", { class: "bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white mb-4" }, [
                            m("div", { class: "flex justify-between items-center" }, [
                                m("div", [
                                    m("p", { class: "text-xs text-blue-100 mb-1" }, "Total Pembayaran"),
                                    m("p", { class: "text-xl font-bold" }, formatRupiah(
                                        this.installments.reduce((sum, inst) => sum + parseFloat(inst.scheduled_amount || 0), 0)
                                    ))
                                ]),
                                m("div", { class: "text-right" }, [
                                    m("p", { class: "text-xs text-blue-100 mb-1" }, "Jumlah Transaksi"),
                                    m("p", { class: "text-2xl font-bold" }, this.installments.length)
                                ])
                            ])
                        ]),

                        // Installment List
                        this.installments.map(installment => 
                            m("div", {
                                key: installment.id,
                                class: "bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700"
                            }, [
                                m("div", { class: "flex items-start justify-between mb-3" }, [
                                    m("div", [
                                        m("p", { class: "font-semibold text-gray-800 dark:text-white text-sm" }, 
                                            installment.customer_name || 'Nasabah'
                                        ),
                                        m("p", { class: "text-xs text-gray-600 dark:text-gray-300 mt-0.5" }, 
                                            `Angsuran #${installment.installment_number}`
                                        )
                                    ]),
                                    m("div", { class: "text-right" }, [
                                        m("p", { class: "font-bold text-blue-600 dark:text-blue-400 text-sm" }, 
                                            formatRupiah(installment.scheduled_amount || 0)
                                        ),
                                        m("p", { class: "text-xs text-gray-500 dark:text-gray-400 mt-0.5" }, 
                                            new Date(installment.payment_date || installment.due_date).toLocaleDateString('id-ID')
                                        )
                                    ])
                                ]),
                                
                                // Payment details
                                m("div", { class: "pt-3 border-t border-gray-100 dark:border-gray-700" }, [
                                    m("div", { class: "grid grid-cols-2 gap-2 text-xs" }, [
                                        m("div", [
                                            m("span", { class: "text-gray-600 dark:text-gray-400" }, "Sumber: "),
                                            m("span", { class: "font-medium text-gray-800 dark:text-white" }, 
                                                installment.payment_method === 'auto_debit' ? 'Auto Debet' : 'Tunai'
                                            )
                                        ]),
                                        m("div", [
                                            m("span", { class: "text-gray-600 dark:text-gray-400" }, "Pokok: "),
                                            m("span", { class: "font-medium text-gray-800 dark:text-white" }, 
                                                formatRupiah(installment.principal_amount || 0)
                                            )
                                        ]),
                                        m("div", [
                                            m("span", { class: "text-gray-600 dark:text-gray-400" }, "Bunga: "),
                                            m("span", { class: "font-medium text-gray-800 dark:text-white" }, 
                                                formatRupiah(installment.interest_amount || 0)
                                            )
                                        ])
                                    ])
                                ])
                            ])
                        )
                    ])
            ])
        ]);

        return m(CoordMobileLayout, { activeTab: null }, content);
    }
};

export default CoordMobileRekapPinjamanDetails;
